import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { formatClock } from '../lib/format.js';

/**
 * An audio player built for copying CW rather than for listening to music:
 * slow-down that keeps the pitch, a loop you drag across the hard stretch,
 * and the transport keys transcribers already know (oTranscribe's: Esc
 * plays and pauses, F1/F2 skip, F3/F4 change speed), which work while the
 * cursor is in the transcript box.
 *
 * The file is fetched ONCE and feeds both the <audio> element (as a blob URL)
 * and the envelope strip, so a slow connection downloads nothing twice.
 */

const RATES = [0.5, 0.6, 0.7, 0.8, 0.9, 1] as const;
const SKIP_SEC = 3;
const COLUMNS = 1200;
const MIN_LOOP_SEC = 0.3;

type PitchAudio = HTMLAudioElement & {
  preservesPitch?: boolean;
  mozPreservesPitch?: boolean;
  webkitPreservesPitch?: boolean;
};

interface Loop {
  a: number;
  b: number;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** Every clip puts its station on this tone (tools/cwaudio.py PITCH_HZ). */
const PITCH_HZ = 600;

/**
 * The strip: the level of the station being copied, over time, scaled between
 * the clip's own noise floor and its loud end.
 *
 * It listens only to +-60 Hz around the tone. The audio carries a 500 Hz
 * passband, and across all of it a weak signal's keying drowned in the noise
 * peaks: the first version drew every clip as a solid block. Two band-pass
 * biquads buy about 7 dB, and scaling from the 20th percentile (the floor)
 * rather than from the peak puts the key-up gaps at the bottom of the strip.
 */
async function computeEnvelope(data: ArrayBuffer): Promise<Float32Array> {
  let decodeCtx: OfflineAudioContext;
  try {
    decodeCtx = new OfflineAudioContext(1, 1, 8000);
  } catch {
    decodeCtx = new OfflineAudioContext(1, 1, 44100); // engines that refuse 8 kHz contexts
  }
  const decoded = await decodeCtx.decodeAudioData(data);

  const ctx = new OfflineAudioContext(1, decoded.length, decoded.sampleRate);
  const source = ctx.createBufferSource();
  source.buffer = decoded;
  let node: AudioNode = source;
  for (let i = 0; i < 2; i += 1) {
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = PITCH_HZ;
    band.Q.value = 5;
    node.connect(band);
    node = band;
  }
  node.connect(ctx.destination);
  source.start();
  const samples = (await ctx.startRendering()).getChannelData(0);

  const db = new Float32Array(COLUMNS);
  const per = samples.length / COLUMNS;
  for (let i = 0; i < COLUMNS; i += 1) {
    let sum = 0;
    const start = Math.floor(i * per);
    const end = Math.max(start + 1, Math.min(samples.length, Math.floor((i + 1) * per)));
    for (let j = start; j < end; j += 1) sum += samples[j]! * samples[j]!;
    db[i] = 10 * Math.log10(Math.max(sum / (end - start), 1e-12));
  }
  const sorted = Array.from(db).sort((x, y) => x - y);
  const floor = sorted[Math.floor(COLUMNS * 0.2)]!;
  const top = Math.max(sorted[Math.floor(COLUMNS * 0.995)]!, floor + 3);
  const out = new Float32Array(COLUMNS);
  for (let i = 0; i < COLUMNS; i += 1) out[i] = clamp((db[i]! - floor) / (top - floor), 0, 1);
  return out;
}

export default function Player({ src, listened }: { src: string; listened: MutableRefObject<number> }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [envelope, setEnvelope] = useState<Float32Array | null>(null);
  const [duration, setDuration] = useState(0);
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState<number>(1);
  const [loop, setLoop] = useState<Loop | null>(null);
  const [loopOn, setLoopOn] = useState(false);
  const [pendingA, setPendingA] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // The animation loop reads these without re-subscribing every frame.
  const loopRef = useRef<{ loop: Loop | null; on: boolean }>({ loop: null, on: false });
  loopRef.current = { loop, on: loopOn };

  // --- load -------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setStatus('loading');
    setProgress(null);
    setEnvelope(null);
    setUrl(null);
    setError(null);
    setLoop(null);
    setLoopOn(false);
    setTime(0);

    void (async () => {
      try {
        const response = await fetch(src);
        if (!response.ok) throw new Error(`The audio did not load (HTTP ${response.status}).`);
        const total = Number(response.headers.get('Content-Length')) || 0;
        let buffer: ArrayBuffer;
        const reader = response.body?.getReader();
        if (reader) {
          const chunks: Uint8Array[] = [];
          let got = 0;
          for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            chunks.push(value);
            got += value.length;
            if (total && !cancelled) setProgress(got / total);
          }
          const all = new Uint8Array(got);
          let offset = 0;
          for (const chunk of chunks) {
            all.set(chunk, offset);
            offset += chunk.length;
          }
          buffer = all.buffer as ArrayBuffer;
        } else {
          buffer = await response.arrayBuffer();
        }
        if (cancelled) return;

        objectUrl = URL.createObjectURL(new Blob([buffer], { type: 'audio/wav' }));
        setUrl(objectUrl);
        try {
          // decodeAudioData takes ownership of what it is given: hand it a copy.
          const env = await computeEnvelope(buffer.slice(0));
          if (!cancelled) setEnvelope(env);
        } catch {
          // The strip is an aid; the player works without it.
        }
      } catch (err) {
        if (!cancelled) {
          setStatus('error');
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  // --- speed, keeping the pitch where it is -----------------------------
  useEffect(() => {
    const audio = audioRef.current as PitchAudio | null;
    if (!audio) return;
    audio.preservesPitch = true;
    audio.mozPreservesPitch = true;
    audio.webkitPreservesPitch = true;
    audio.defaultPlaybackRate = rate;
    audio.playbackRate = rate;
  }, [rate, url]);

  // --- clock, loop and listened-time accounting while playing ------------
  // Driven by animation frames for a smooth playhead, AND by the audio's own
  // timeupdate events: browsers stop animation frames in a hidden tab, and the
  // loop must still wrap (a quarter-second late at worst) when nobody is
  // looking at the strip.
  const lastRef = useRef(0);
  const advance = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const { loop: l, on } = loopRef.current;
    if (on && l && audio.currentTime >= l.b) {
      audio.currentTime = l.a;
      lastRef.current = l.a;
    } else {
      const step = audio.currentTime - lastRef.current;
      if (step > 0 && step < 1) listened.current += step;
      lastRef.current = audio.currentTime;
    }
    setTime(audio.currentTime);
  }, [listened]);

  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    const tick = (): void => {
      advance();
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, advance]);

  // --- transport ----------------------------------------------------------
  const seek = useCallback((t: number) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(audio.duration)) return;
    audio.currentTime = clamp(t, 0, audio.duration);
    lastRef.current = audio.currentTime; // a jump is not listening
    setTime(audio.currentTime);
  }, []);

  const skip = useCallback((delta: number) => seek((audioRef.current?.currentTime ?? 0) + delta), [seek]);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || status !== 'ready') return;
    if (!audio.paused) {
      audio.pause();
      return;
    }
    const { loop: l, on } = loopRef.current;
    if (on && l && (audio.currentTime < l.a || audio.currentTime >= l.b)) audio.currentTime = l.a;
    audio.play().then(
      () => setNotice(null),
      // Browsers refuse to start audio before the page has had a click; Esc
      // does not count as one. Say so instead of doing nothing.
      () => setNotice('Your browser wants a click before it will play: press the play button once.'),
    );
  }, [status]);

  const stepRate = useCallback((dir: 1 | -1) => {
    setRate((r) => {
      const i = RATES.indexOf(r as (typeof RATES)[number]);
      return RATES[clamp((i < 0 ? RATES.length - 1 : i) + dir, 0, RATES.length - 1)]!;
    });
  }, []);

  const setA = (): void => setPendingA(audioRef.current?.currentTime ?? 0);
  const setB = (): void => {
    const t = audioRef.current?.currentTime ?? 0;
    const a = pendingA ?? loop?.a ?? null;
    if (a === null || t - a < MIN_LOOP_SEC) return;
    setLoop({ a, b: t });
    setLoopOn(true);
    setPendingA(null);
  };
  const clearLoop = (): void => {
    setLoop(null);
    setLoopOn(false);
    setPendingA(null);
  };

  // --- keys ---------------------------------------------------------------
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const el = e.target instanceof HTMLElement ? e.target : null;
      const typing = !!el && (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT' || el.tagName === 'SELECT');
      const onButton = el?.tagName === 'BUTTON';
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          toggle();
          break;
        case 'F1':
          e.preventDefault();
          skip(-SKIP_SEC);
          break;
        case 'F2':
          e.preventDefault();
          skip(SKIP_SEC);
          break;
        case 'F3':
          e.preventDefault();
          stepRate(-1);
          break;
        case 'F4':
          e.preventDefault();
          stepRate(1);
          break;
        case 'F6':
          e.preventDefault();
          if (loopRef.current.loop) setLoopOn((v) => !v);
          break;
        case ' ':
          if (!typing && !onButton) {
            e.preventDefault();
            toggle();
          }
          break;
        case 'ArrowLeft':
          if (!typing) {
            e.preventDefault();
            skip(-SKIP_SEC);
          }
          break;
        case 'ArrowRight':
          if (!typing) {
            e.preventDefault();
            skip(SKIP_SEC);
          }
          break;
        default:
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle, skip, stepRate]);

  // --- the strip ------------------------------------------------------------
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const draw = (): void => {
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w === 0 || h === 0) return;
      if (canvas.width !== Math.round(w * dpr)) canvas.width = Math.round(w * dpr);
      if (canvas.height !== Math.round(h * dpr)) canvas.height = Math.round(h * dpr);
      const g = canvas.getContext('2d');
      if (!g) return;
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      g.clearRect(0, 0, w, h);

      const css = getComputedStyle(canvas);
      const color = (name: string, fallback: string): string => css.getPropertyValue(name).trim() || fallback;
      const x = (t: number): number => (duration > 0 ? (t / duration) * w : 0);

      if (loop && duration > 0) {
        g.fillStyle = color(loopOn ? '--strip-loop' : '--strip-loop-off', 'rgba(242,184,75,0.18)');
        g.fillRect(x(loop.a), 0, Math.max(2, x(loop.b) - x(loop.a)), h);
      }
      if (pendingA !== null && duration > 0) {
        g.fillStyle = color('--strip-mark', '#f2b84b');
        g.fillRect(x(pendingA) - 1, 0, 2, h);
      }

      if (envelope) {
        const n = envelope.length;
        const bw = w / n;
        const played = duration > 0 ? (time / duration) * n : 0;
        const bar = color('--strip-bar', '#3a4656');
        const barPlayed = color('--strip-played', '#7fb4ff');
        for (let i = 0; i < n; i += 1) {
          const bh = Math.max(1, envelope[i]! * (h - 6));
          g.fillStyle = i < played ? barPlayed : bar;
          g.fillRect(i * bw, (h - bh) / 2, Math.max(1, bw - 0.25), bh);
        }
      } else {
        g.fillStyle = color('--strip-bar', '#3a4656');
        g.fillRect(0, h / 2 - 1, w, 2);
      }

      if (duration > 0) {
        g.fillStyle = color('--strip-head', '#ffffff');
        g.fillRect(x(time) - 1, 0, 2, h);
      }
    };
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [envelope, time, loop, loopOn, pendingA, duration]);

  const drag = useRef<{ x0: number; t0: number; moved: boolean } | null>(null);
  const timeAt = (clientX: number): number => {
    const canvas = canvasRef.current;
    if (!canvas || duration <= 0) return 0;
    const r = canvas.getBoundingClientRect();
    return clamp((clientX - r.left) / r.width, 0, 1) * duration;
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>): void => {
    if (status !== 'ready') return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x0: e.clientX, t0: timeAt(e.clientX), moved: false };
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>): void => {
    const d = drag.current;
    if (!d) return;
    if (!d.moved && Math.abs(e.clientX - d.x0) < 5) return;
    d.moved = true;
    const t = timeAt(e.clientX);
    setLoop({ a: Math.min(d.t0, t), b: Math.max(d.t0, t) });
    setLoopOn(true);
    setPendingA(null);
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLCanvasElement>): void => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (!d.moved) {
      seek(timeAt(e.clientX));
      return;
    }
    const t = timeAt(e.clientX);
    const a = Math.min(d.t0, t);
    const b = Math.max(d.t0, t);
    if (b - a < MIN_LOOP_SEC) {
      clearLoop();
      return;
    }
    setLoop({ a, b });
    setLoopOn(true);
    seek(a);
  };

  return (
    <section className="player" aria-label="Clip player">
      <audio
        ref={audioRef}
        src={url ?? undefined}
        preload="auto"
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onCanPlay={() => setStatus((s) => (s === 'loading' ? 'ready' : s))}
        onPlay={(e) => {
          lastRef.current = e.currentTarget.currentTime;
          setPlaying(true);
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={advance}
        onSeeked={(e) => {
          lastRef.current = e.currentTarget.currentTime;
          setTime(e.currentTarget.currentTime);
        }}
        onError={() => {
          if (url) {
            setStatus('error');
            setError('This browser could not play the audio.');
          }
        }}
      />

      <div className="strip-wrap">
        <canvas
          ref={canvasRef}
          className="strip"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={() => {
            drag.current = null;
          }}
          aria-label="Clip envelope: click to jump, drag to loop"
        />
        {status === 'loading' && (
          <div className="strip-overlay">
            Loading audio{progress !== null ? ` - ${Math.round(progress * 100)}%` : '...'}
          </div>
        )}
        {status === 'error' && <div className="strip-overlay is-error">{error}</div>}
      </div>

      <div className="player-controls">
        <button
          type="button"
          className="btn-play"
          onClick={toggle}
          disabled={status !== 'ready'}
          aria-label={playing ? 'Pause (Esc)' : 'Play (Esc)'}
          title={playing ? 'Pause (Esc)' : 'Play (Esc)'}
        >
          {playing ? (
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <rect x="3" y="2" width="3.5" height="12" rx="1" />
              <rect x="9.5" y="2" width="3.5" height="12" rx="1" />
            </svg>
          ) : (
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path d="M4 2.5v11a.8.8 0 0 0 1.2.7l9-5.5a.8.8 0 0 0 0-1.4l-9-5.5A.8.8 0 0 0 4 2.5z" />
            </svg>
          )}
        </button>
        <button type="button" onClick={() => skip(-SKIP_SEC)} disabled={status !== 'ready'} title="Back 3 s (F1)">
          &minus;3 s
        </button>
        <button type="button" onClick={() => skip(SKIP_SEC)} disabled={status !== 'ready'} title="Forward 3 s (F2)">
          +3 s
        </button>
        <span className="clock" aria-live="off">
          {formatClock(time)} <span className="muted">/ {formatClock(duration)}</span>
        </span>

        <div className="seg" role="group" aria-label="Playback speed (F3 slower, F4 faster)">
          {RATES.map((r) => (
            <button
              type="button"
              key={r}
              className={r === rate ? 'is-on' : ''}
              aria-pressed={r === rate}
              onClick={() => setRate(r)}
            >
              {r === 1 ? '1×' : `${r}×`}
            </button>
          ))}
        </div>

        <div className="loop-controls" role="group" aria-label="Loop">
          <button type="button" onClick={setA} disabled={status !== 'ready'} title="Loop starts here">
            A
          </button>
          <button
            type="button"
            onClick={setB}
            disabled={status !== 'ready' || (pendingA === null && !loop)}
            title="Loop ends here"
          >
            B
          </button>
          {loop ? (
            <>
              <button
                type="button"
                className={loopOn ? 'is-on' : ''}
                aria-pressed={loopOn}
                onClick={() => setLoopOn((v) => !v)}
                title="Loop on/off (F6)"
              >
                Loop {formatClock(loop.a)}&ndash;{formatClock(loop.b)}
              </button>
              <button type="button" onClick={clearLoop} title="Clear the loop">
                &times;
              </button>
            </>
          ) : (
            <span className="muted small">
              {pendingA !== null ? `Loop from ${formatClock(pendingA)} - now press B` : 'No loop'}
            </span>
          )}
        </div>
      </div>

      {notice && <p className="player-notice">{notice}</p>}
      <p className="player-hint">
        Click the strip to jump; drag across it to loop a stretch. Keys work while you type:{' '}
        <kbd>Esc</kbd> play/pause &middot; <kbd>F1</kbd>/<kbd>F2</kbd> back/forward 3 s &middot; <kbd>F3</kbd>/
        <kbd>F4</kbd> slower/faster &middot; <kbd>F6</kbd> loop on/off
      </p>
    </section>
  );
}
